import type { ResourceTypeHandle, Tool } from '@toolproof-core/genesis/_contract/_index';

import { extractToolStepsFromWorkflow } from '@toolproof-core/lib/_pure/workflow-extraction';
import { resolveWorkflowStateChain } from '@toolproof-core/lib/_pure/workflow-state-resolution';
import type { ToolMap } from '@toolproof-core/lib/_contract/workflow-execution';
import type { MutableToolStep, MutableWorkflow } from '@toolproof-core/lib/_contract/workflow-overlay';

import type { PrimitiveResourceTypeHandle } from '@/workflowExecution/_contract/types';

export function requirePrimitiveType(handle: ResourceTypeHandle | undefined): PrimitiveResourceTypeHandle {
    if (handle !== 'TYPE-Natural' && handle !== 'TYPE-Boolean') {
        throw new Error(`Local primitive execution does not support ResourceType '${handle}'.`);
    }
    return handle;
}

export function validatePrimitiveProjection(handle: PrimitiveResourceTypeHandle, projection: unknown): void {
    const valid = handle === 'TYPE-Boolean'
        ? typeof projection === 'boolean'
        : typeof projection === 'number' && Number.isInteger(projection) && projection >= 0;
    if (!valid) throw new Error(`Expected ${handle === 'TYPE-Boolean' ? 'a Boolean' : 'a non-negative integer'} for '${handle}'.`);
}

export function requireTool(toolMap: ToolMap, step: MutableToolStep): Tool {
    const tool = toolMap.get(step.toolHandle);
    if (!tool) throw new Error(`Tool '${step.toolHandle}' has no supported Genesis declaration and local primitive implementation.`);
    return tool;
}

export function validatePrimitiveWorkflow(workflow: MutableWorkflow, toolMap: ToolMap): void {
    if (!workflow.stepsByThreadIndex[0]?.length) throw new Error('Local primitive execution requires a nonempty first thread.');
    const steps = extractToolStepsFromWorkflow(workflow);
    const stepByKey = new Map<string, MutableToolStep>(steps.map(step => [step.stepKey, step]));
    for (const step of steps) {
        const tool = requireTool(toolMap, step);
        for (const [names, roles] of [
            [step.roleBindingSpec.inputBindings, tool.roleSpec.inputRoleByName],
            [step.roleBindingSpec.outputBindings, tool.roleSpec.outputRoleByName],
        ] as const) {
            if (names.length !== Object.keys(roles).length || new Set(names).size !== names.length
                || names.some(name => !Object.hasOwn(roles, name))) {
                throw new Error(`ToolStep '${step.stepKey}' bindings do not match Genesis Tool '${tool.handle}'.`);
            }
            for (const role of Object.values(roles)) requirePrimitiveType(role.resourceTypeHandle);
        }
    }
    for (const [key, entries] of Object.entries(workflow.workflowState)) {
        const step = stepByKey.get(key);
        if (!step) throw new Error(`Workflow state references an unknown ToolStep '${key}'.`);
        const tool = requireTool(toolMap, step);
        if (!entries) throw new Error(`Missing workflow state entries at '${key}'.`);
        for (const [name, entry] of Object.entries(entries)) {
            if (!entry) throw new Error(`Missing workflow state entry at '${key}:${name}'.`);
            const role = tool.roleSpec.inputRoleByName[name] ?? tool.roleSpec.outputRoleByName[name];
            if (!role) throw new Error(`Undeclared role '${name}' in Tool '${tool.handle}'.`);
            const type = requirePrimitiveType(role.resourceTypeHandle);
            if (entry.workflowStateEntryKind === 'materializedResource') {
                if (entry.resourceTypeHandle !== type) throw new Error(`ResourceType mismatch at '${key}:${name}'.`);
                validatePrimitiveProjection(type, entry.projection);
            }
            if (entry.workflowStateEntryKind === 'internalInputPotential') {
                const source = entry.toolStepRoleAddress;
                const sourceStep = stepByKey.get(source.stepKey);
                if (!sourceStep) throw new Error(`Unknown input source '${source.stepKey}'.`);
                const sourceTool = requireTool(toolMap, sourceStep);
                const sourceRole = sourceTool.roleSpec.inputRoleByName[source.roleName] ?? sourceTool.roleSpec.outputRoleByName[source.roleName];
                if (!sourceRole || sourceRole.resourceTypeHandle !== type) {
                    throw new Error(`Input source ResourceType mismatch at '${key}:${name}'.`);
                }
            }
            const resolution = resolveWorkflowStateChain(workflow.workflowState, { stepKey: step.stepKey, roleName: name });
            if (resolution.status === 'unresolved' && resolution.reason !== 'not-found') {
                throw new Error(`Invalid workflow dependency at '${key}:${name}': ${resolution.reason}.`);
            }
        }
    }
}
